// Checkout country metadata is centralized here so behavior is deterministic and maintainable.
// Subdivision labels are derived from countries.json state subdivision metadata,
// with a small set of explicit overrides where subdivision types are missing or ambiguous.

import countriesJSON from './countries.json';

type CountryRecord = {
    code2: string,
    states?: Array<{
        subdivision?: string | null,
    }>,
};

const SUBDIVISION_TYPE_TO_LABEL: Record<string, string> = {
    state: 'State',
    province: 'Province',
    region: 'Region',
    county: 'County',
    district: 'District',
    territory: 'Territory',
    municipality: 'Municipality',
    republic: 'Republic',
    nation: 'Country',
    country: 'Country',
};

const SUBDIVISION_KEYWORD_TO_LABEL: Array<[string, string]> = [
    ['province', 'Province'],
    ['state', 'State'],
    ['region', 'Region'],
    ['county', 'County'],
    ['district', 'District'],
    ['territory', 'Territory'],
    ['municipality', 'Municipality'],
    ['borough', 'District'],
    ['council area', 'District'],
    ['republic', 'Republic'],
    ['nation', 'Country'],
    ['country', 'Country'],
];

const LABEL_PRIORITY: Record<string, number> = {
    State: 7,
    Province: 6,
    Region: 5,
    County: 4,
    District: 3,
    Territory: 2,
    Municipality: 1,
    Republic: 0,
    Country: 0,
};

const CHECKOUT_SUBDIVISION_LABEL_OVERRIDES: Record<string, string> = {
    AU: 'State',
    CA: 'Province',
    GB: 'County',
    IE: 'County',
    NZ: 'Region',
    US: 'State',
    ZA: 'Province',
};

const inferLabelFromSubdivision = (subdivision: string): string | undefined => {
    const normalized = subdivision.toLowerCase().trim();

    if (SUBDIVISION_TYPE_TO_LABEL[normalized]) {
        return SUBDIVISION_TYPE_TO_LABEL[normalized];
    }

    const keywordMatch = SUBDIVISION_KEYWORD_TO_LABEL
        .find(([keyword]) => normalized.includes(keyword));
    return keywordMatch ? keywordMatch[1] : undefined;
};

const inferCountrySubdivisionLabel = (country: CountryRecord): string => {
    if (!country.states || country.states.length === 0) {
        return 'State / Province';
    }

    const labelCounts: Record<string, number> = country.states
        .reduce((counts, state) => {
            const label = state.subdivision
                ? inferLabelFromSubdivision(state.subdivision)
                : undefined;
            if (!label) return counts;
            return {
                ...counts,
                [label]: (counts[label] || 0) + 1,
            };
        }, {} as Record<string, number>);

    const labels = Object.keys(labelCounts);
    if (labels.length === 0) {
        return 'State';
    }

    return labels.sort((a, b) => {
        const countDiff = (labelCounts[b] || 0) - (labelCounts[a] || 0);
        if (countDiff !== 0) return countDiff;
        return (LABEL_PRIORITY[b] || 0) - (LABEL_PRIORITY[a] || 0);
    })[0];
};

export const COUNTRY_SUBDIVISION_LABEL_BY_CODE2: Record<string, string> = (countriesJSON as CountryRecord[])
    .reduce((labelsByCode, country) => {
        const label = CHECKOUT_SUBDIVISION_LABEL_OVERRIDES[country.code2]
            || inferCountrySubdivisionLabel(country);
        return {
            ...labelsByCode,
            [country.code2]: label,
        };
    }, {} as Record<string, string>);
